import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Dialog } from 'primereact/dialog';
import { InputText } from "primereact/inputtext";
import { useAuth } from '../../../context/authContext';
import { VisualizarChamado } from '../visualizacao/visualizar-chamado';
import CalledService from '../../../services/resource/calledService';
import moment from 'moment-timezone';
import useCommentWebSocket from '../../../utils/useCommentWebSocket ';
import { priorityMap, statusMap } from '../../../utils/options';

const itemsPerPage = 5;

function MinhasSolicitacoes() {
    useCommentWebSocket();
    const { user } = useAuth();
    const calledService = new CalledService();
    const navigate = useNavigate();
    const location = useLocation();

    const [calleds, setCalleds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ticket, setTicket] = useState(null);
    const [showPreviewCalled, setShowPreviewCalled] = useState(false);
    const [filterValue, setFilterValue] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (location.hash) {
            const split = location.hash.split('#/');
            setTicket(split[1]);
            setShowPreviewCalled(true);
        }
    }, [location]);

    const fetchData = async () => {
        try {
            const response = await calledService.getCalledGetEmployee(user.sub);
            setCalleds(response.data);
        } finally {
            setTimeout(() => setLoading(false), 400);
        }
    };

    const filteredCalleds = calleds.filter(ticket =>
        ticket.status.includes(filterStatus) &&
        (
            ticket.ticket.toLowerCase().includes(filterValue.toLowerCase()) ||
            ticket.title.toLowerCase().includes(filterValue.toLowerCase()) ||
            ticket.creationDate.includes(filterValue)
        )
    );

    const totalItems = filteredCalleds.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
    const paginatedCalleds = filteredCalleds.slice(startIndex, endIndex);

    const handlePrev = () => setCurrentPage(prev => Math.max(prev - 1, 1));
    const handleNext = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));

    useEffect(() => {
        setCurrentPage(1); // Reinicia paginação ao filtrar
    }, [filterStatus, filterValue]);

    const handleFilterStatus = (e) => setFilterStatus(e.target.value);
    const handleSearch = (e) => setFilterValue(e.target.value);
    const openCalled = (ticket) => {
        setTicket(ticket);
        navigate(`/minhas-solicitacoes/ticket/${ticket}`);
    };

    const closePreview = () => {
        setShowPreviewCalled(false);
        window.location.href = '#';
        setTicket(null);
    };

    const getPriority = (priority) => priorityMap[priority]?.value || 'desconhecido';
    const getStatus = (status) => statusMap[status]?.value || 'desconhecido';
    const getStatusLabel = (status) => statusMap[status]?.label || 'Desconhecido';

    return (
        <div className="container mx-auto px-4 py-1 mb-7">
            {/* Header e filtros */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
                <h2 className="text-2xl font-semibold text-gray-800 flex items-center">
                    <i className="fas fa-list-ol mr-2 text-indigo-600" /> Minhas Solicitações
                </h2>

                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 w-full md:w-auto">
                    <select
                        value={filterStatus}
                        onChange={handleFilterStatus}
                        className="bg-white border-1 border-gray-300 rounded-lg px-4 py-2 w-full sm:w-auto"
                    >
                        <option value="">Todos os status</option>
                        <option value="ABERTO">Aguardando Atribuição</option>
                        <option value="EXECUCAO">Em Execução</option>
                        <option value="ESCALADO">Escalados</option>
                        <option value="RESOLVIDO">Resolvidos</option>
                        <option value="CANCELADO">Cancelados</option>
                    </select>

                    <InputText
                        className="bg-white border-1 border-gray-300 rounded-lg px-4 py-2 w-full sm:w-64"
                        value={filterValue}
                        onChange={handleSearch}
                        placeholder="Buscar por ticket ou título"
                    />
                </div>
            </div>

            <StatsCard
                total={calleds.length}
                pending={calleds.filter(t => t.status === 'ABERTO' || t.status === 'EXECUCAO').length}

                completed={calleds.filter(t => t.status === 'RESOLVIDO').length}
                canceled={calleds.filter(t => t.status === 'CANCELADO').length}
                climbed={calleds.filter(t => t.status === 'ESCALADO').length}
            />

            {/* Lista */}
            {loading ? (
                <div className="text-center py-10">
                    <i className="fas fa-spinner fa-spin text-4xl text-indigo-600 mb-4" />
                    <p className="text-gray-600">Carregando chamados...</p>
                </div>
            ) : totalItems === 0 ? (
                <div className="text-center py-10">
                    <i className="fas fa-inbox text-4xl text-gray-400 mb-4" />
                    <p className="text-gray-600">Nenhum chamado encontrado</p>
                </div>
            ) : (
                <>
                    <div className="space-y-4">
                        {paginatedCalleds.map(called => (
                            <div
                                key={called.ticket}
                                onClick={() => openCalled(called.ticket)}
                                className={`ticket-card bg-white rounded-lg shadow p-4 cursor-pointer
                                   hover:shadow-lg hover:scale-[1.02] transition-all duration-200
                                   priority-${getPriority(called.priority)}`}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="text-lg font-semibold text-gray-800">{called.title}</h3>
                                    <div>
                                        <span className={`text-xs font-semibold px-2 py-1 rounded-full status-${getStatus(called.status)}`}>
                                            {getStatusLabel(called.status)}
                                        </span>
                                        <span className='text-xs ml-5 text-gray-600'>
                                            #{called.ticket}
                                        </span>
                                    </div>
                                </div>
                                {called.unreadMessageSender > 0 && (
                                    <p className="text-red-500 text-sm mb-2">{called.unreadMessageSender} mensagem(ns) não lida(s)</p>
                                )}

                                <p className="text-gray-600 text-md mb-4">{called.description}</p>
                                <div className="flex flex-wrap items-center justify-between text-sm text-gray-500">
                                    <div className="flex items-center mb-2 md:mb-0">
                                        <span>{called.serviceTypeDescription}</span>
                                    </div>
                                </div>
                                <div className="text-sm text-gray-500">
                                    <i className="far fa-clock mr-3" />
                                    Criado em {moment(called.creationDate).format('DD/MM/yyyy HH:mm')}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Paginação */}
                    <div className="flex justify-between items-center mt-8">
                        <div className="text-sm text-gray-600">
                            Mostrando <span>{totalItems === 0 ? 0 : startIndex + 1}</span> a <span>{endIndex}</span> de <span>{totalItems}</span> chamados
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={handlePrev}
                                disabled={currentPage === 1}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                            >
                                <i className="fas fa-chevron-left" /> Anterior
                            </button>
                            <button
                                onClick={handleNext}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                            >
                                Próximo <i className="fas fa-chevron-right" />
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Modal */}
            <Dialog
                className="dialog-preview-called"
                header={`Ticket ${ticket}`}
                visible={showPreviewCalled}
                onHide={closePreview}
                footer={null}
            >
                <VisualizarChamado ticket={ticket} />
            </Dialog>
        </div>
    );
}

export default MinhasSolicitacoes;

const StatsCard = ({ total = 0, pending = 0, completed = 0, canceled = 0, climbed = 0 }) => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
                <div className="flex justify-between items-start">
                    <div>
                        <p className="text-sm text-gray-500">Total</p>
                        <h3 className="text-2xl font-bold text-gray-800">{total}</h3>
                    </div>
                    <div className="bg-blue-100 p-2 rounded-full">
                        <i className="fas fa-ticket-alt text-blue-600"></i>
                    </div>
                </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
                <div className="flex justify-between items-start">
                    <div>
                        <p className="text-sm text-gray-500">Pendentes</p>
                        <h3 className="text-2xl font-bold text-gray-800">{pending}</h3>
                    </div>
                    <div className="bg-yellow-100 p-2 rounded-full">
                        <i className="fas fa-clock text-yellow-600"></i>
                    </div>
                </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-900">
                <div className="flex justify-between items-start">
                    <div>
                        <p className="text-sm text-gray-500">Escalados</p>
                        <h3 className="text-2xl font-bold text-gray-800">{climbed}</h3>
                    </div>
                    <div className="bg-red-100 p-2 rounded-full">
                        <i className="fas fa-chevron-circle-up  text-yellow-900"></i>
                    </div>
                </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
                <div className="flex justify-between items-start">
                    <div>
                        <p className="text-sm text-gray-500">Resolvidos</p>
                        <h3 className="text-2xl font-bold text-gray-800">{completed}</h3>
                    </div>
                    <div className="bg-green-100 p-2 rounded-full">
                        <i className="fas fa-check-circle text-green-600"></i>
                    </div>
                </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
                <div className="flex justify-between items-start">
                    <div>
                        <p className="text-sm text-gray-500">Cancelados</p>
                        <h3 className="text-2xl font-bold text-gray-800">{canceled}</h3>
                    </div>
                    <div className="bg-red-100 p-2 rounded-full">
                        <i className="fas fa-times-circle text-red-600"></i>
                    </div>
                </div>
            </div>
        </div>
    );
};
